import * as React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Breadcrumb, Breadcrumbs} from '~/components/Breadcrumps';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useSession} from '~/modules/Profile/session';
import {fetchRillIframeSrc} from '~/modules/RillAnalytics/api';
import {useImpersonatedResourceOptions} from '~/modules/RillAnalytics/useImpersonatedResourceOptions';
import {RootAbility} from '~/root_ability';
import {ResourceSwitcher} from '~/views/HeaderView/ResourceSwitcher';
import style from './index.module.scss';

export const RillAnalyticsView = () => {
  const {session} = useSession();
  const ability = useAbility(RootAbility);

  const isAdmin = ability.isAdmin();
  const impersonatedUserId = session.selectedImpersonatedUserId;
  const {items: impersonatedResourceItems, isPending: isImpersonatedResourcePending} =
    useImpersonatedResourceOptions(isAdmin ? impersonatedUserId : null);

  const needsUserSelection = isAdmin && impersonatedUserId == null;
  const isResourcePending = isAdmin && impersonatedUserId != null && isImpersonatedResourcePending;

  const resourceId = ability.isSeller() || ability.isSelfServe()
    ? session.selectedSellerId
    : session.selectedImpersonatedResourceId ?? impersonatedResourceItems[0]?.value ?? null;

  const iframeQuery = useQuery({
    queryKey: ['rill-iframe', resourceId, impersonatedUserId],
    queryFn: () => fetchRillIframeSrc(resourceId ?? undefined, impersonatedUserId ?? undefined)
      .then(res => res.data.iframeSrc),
    enabled: !needsUserSelection && !isResourcePending,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const iframeSrc = iframeQuery.data ?? null;
  const error = iframeQuery.isError;

  return (
    <Breadcrumb breadcrumb={{title: 'Analytics'}}>
      <div className={style.container}>
        <div className="page-header-sticky">
          <div className="page-header-breadcrumbs">
            <Breadcrumbs />
          </div>
          <Title className="page-header-title" toolbar={<ResourceSwitcher />}>Analytics</Title>
        </div>
        {needsUserSelection && (
          <div className={style.placeholder}>
            <i className={['fa fa-users', style.placeholderIcon].join(' ')} />
            <div className={style.placeholderTitle}>Select a user</div>
            <div className={style.placeholderText}>Choose a user above to view their analytics dashboard</div>
          </div>
        )}
        {!needsUserSelection && error && (
          <div className={style.placeholder}>
            <i className={['fa fa-exclamation-triangle', style.placeholderIcon].join(' ')} />
            <div className={style.placeholderTitle}>Failed to load analytics dashboard</div>
          </div>
        )}
        {!needsUserSelection && iframeSrc && <iframe title="Analytics" src={iframeSrc} className={style.iframe} />}
      </div>
    </Breadcrumb>
  );
};
